/**
 * Site promotion link.
 *
 * The home page and the about page are the only places a signed-in user can hand
 * the product to somebody else, so both render this card. The link carries the
 * account's own public referral code; the visitor who opens it and registers is
 * attributed to this account, with no confirmation shown on either side.
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '../ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card'
import { useCurrentUser } from '../../hooks/use-auth'
import { copyText } from '../../lib/clipboard'
import { promotionUrl } from '../../lib/format'

export function PromotionLinkCard() {
  const { t } = useTranslation()
  const { user } = useCurrentUser()
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>('idle')

  // Anonymous visitors and accounts without a code get the plain site URL.
  const link = promotionUrl(user?.referralCode)

  async function copy() {
    const copied = await copyText(link)
    setCopyState(copied ? 'copied' : 'failed')
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('referral.promotionTitle')}</CardTitle>
        <CardDescription>{t('referral.promotionBody')}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-wrap items-center gap-2">
        <code className="bg-muted rounded-md px-2 py-1 text-xs break-all">{link}</code>
        <Button variant="outline" size="sm" onClick={() => void copy()}>
          {copyState === 'copied' ? t('common.copied') : t('referral.copyPromotionLink')}
        </Button>
        {copyState === 'failed' ? (
          <p className="text-destructive w-full text-xs">{t('export.copyFailed')}</p>
        ) : null}
      </CardContent>
    </Card>
  )
}
